import React from "react";
import { Dialog, DialogContent } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import "./PerformanceModal.scss";

function ConfidenceGauge({ value }) {
  const cx = 60, cy = 62, r = 50, strokeW = 10, innerR = r - strokeW;

  const toRad = d => (d * Math.PI) / 180;
  const pt = (angle, radius) => ({
    x: cx + radius * Math.cos(toRad(angle)),
    y: cy - radius * Math.sin(toRad(angle))
  });

  const arc = (fromDeg, toDeg, outerR, inR) => {
    const o1 = pt(fromDeg, outerR), o2 = pt(toDeg, outerR);
    const i2 = pt(toDeg, inR),     i1 = pt(fromDeg, inR);
    const lg = Math.abs(fromDeg - toDeg) >= 180 ? 1 : 0;
    return `M${o1.x} ${o1.y} A${outerR} ${outerR} 0 ${lg} 0 ${o2.x} ${o2.y} L${i2.x} ${i2.y} A${inR} ${inR} 0 ${lg} 1 ${i1.x} ${i1.y}Z`;
  };

  const segments = [
    { from: 180, to: 135, color: "#ff4d4f" },
    { from: 135, to: 90,  color: "#ff8c00" },
    { from: 90,  to: 45,  color: "#ffd166" },
    { from: 45,  to: 0,   color: "#17c964" },
  ];

  const needleAngle = 180 - value * 1.8;
  const tip = pt(needleAngle, r - 4);

  return (
    <div className="gauge-wrap">
      {/* viewBox makes it scale; width/height set default size; CSS can override */}
      <svg viewBox="0 0 120 70" className="gauge-svg">
        <path d={arc(180, 0, r, innerR)} fill="#2a2f4a" />
        {segments.map((s, i) => (
          <path key={i} d={arc(s.from, s.to, r, innerR)} fill={s.color} />
        ))}
        <line x1={cx} y1={cy} x2={tip.x} y2={tip.y}
          stroke="#fff" strokeWidth="2" strokeLinecap="round" />
        <circle cx={cx} cy={cy} r="4" fill="#fff" />
      </svg>
      <div className="gauge-value">{value}%</div>
      <div className="gauge-label">Confidence Score</div>
    </div>
  );
}

function DonutCircle({ data, color, label, value }) {
  return (
    <div className="circle-item">
      <div className="circle">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} innerRadius="62%" outerRadius="88%" dataKey="value" startAngle={90} endAngle={-270}>
              <Cell fill={color} />
              <Cell fill="#3a3f5a" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="circle-text"><b>{value}</b></div>
      </div>
      <div className="circle-label">{label}</div>
    </div>
  );
}

export default function PerformanceModal({ open, onClose }) {

  const [activeTab, setActiveTab] = React.useState("All");

  const roiData     = [{ value: 15 }, { value: 85 }];
  const successData = [{ value: 65 }, { value: 35 }];

  const coinData = [
    { name: "Large Cap",   value: 30, color: "#2ec7ff" },
    { name: "Mid Cap",     value: 25, color: "#17c964" },
    { name: "Small Cap",   value: 20, color: "#ffd166" },
    { name: "Bitcoin Cash",value: 15, color: "#a259ff" },
    { name: "Avalanche",   value: 10, color: "#ff4d4f" }
  ];

  const tableData = [
    { coin: "Large Cap",    success: "55.56%", roi: "55.56%" },
    { coin: "Mid Cap",      success: "66.67%", roi: "66.67%" },
    { coin: "Small Cap",    success: "61.54%", roi: "61.54%" },
    { coin: "Bitcoin Cash", success: "100%",   roi: "55.56%" },
    { coin: "Avalanche",    success: "100%",   roi: "66.67%" },
    { coin: "Ethereum",     success: "-",      roi: "61.54%" },
    { coin: "Crypto Coin",  success: "-",      roi: "61.54%" }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: "760px",
          maxWidth: "95vw",
          maxHeight: "96vh",
          background: "#1c2137",
          borderRadius: "12px",
          color: "#fff",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column"
        }
      }}
    >
      {/* Use padding:0 on DialogContent; inner div handles padding via SCSS */}
      <DialogContent sx={{ padding: "0 !important", overflow: "hidden", display: "flex", flexDirection: "column", flex: 1, "&::-webkit-scrollbar": { display: "none" }, scrollbarWidth: "none" }}>
        <div className="perf-modal">

          {/* ── TOPBAR ── */}
          <div className="perf-topbar">
            <div className="perf-tabs">
              {["All", "Public", "Private"].map(tab => (
                <button
                  key={tab}
                  className={`tab-btn${activeTab === tab ? " active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
            <div className="time-btns">
              <button>1M</button>
              <button>3M</button>
              <button>6M</button>
              <button>1Y</button>
            </div>
            <span className="close-btn" onClick={onClose}>✕</span>
          </div>

          {/* ── HEADER ── */}
          <div className="perf-header">
            Last updated at – Jan 5, 2024, 06:53 PM IST
          </div>

          {/* ── TOP SECTION ── */}
          <div className="perf-top">

            <div className="recommend-box">
              <div className="rec-title">Total Signals <span>100</span></div>
              <div className="progress">
                <div className="green"></div>
                <div className="red"></div>
              </div>
              <div className="rec-stats">
                <span>78 Advances</span>
                <span>22 Declines</span>
              </div>
              <div className="rec-info">
                <div><span>Live Signals</span><b>4</b></div>
                <div><span>Latest Signals</span><b>20 Sep'23</b></div>
              </div>
            </div>

            {/* ── CHARTS ── */}
            <div className="circle-charts">
              <ConfidenceGauge value={68} />
              <DonutCircle data={roiData}     color="#2d8cff" label="ROI"          value="15%" />
              <DonutCircle data={successData} color="#3CFF00" label="Success Rate" value="65%" />
            </div>

          </div>

          <div className="profit-factor">Profit Factor – <b>1.028</b></div>

          <hr />

          {/* ── BOTTOM ── */}
          <div className="perf-bottom">

            <div className="donut-chart">
              <div className="coin-text">Coin</div>
              <div className="donut-wrap">
                <PieChart width={200} height={200}>
                  <Pie data={coinData} cx={100} cy={100} innerRadius={55} outerRadius={85} dataKey="value">
                    {coinData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                </PieChart>
                <div className="coin-center">Coins</div>
              </div>
            </div>

            <div className="coin-table">
              <div className="table-head">
                <span>Coin</span>
                <span>Success Rate</span>
                <span>ROI</span>
              </div>
              <div className="table-body">
                {tableData.map((row, i) => (
                  <div className="table-row" key={i}>
                    <span>{row.coin}</span>
                    <span>{row.success}</span>
                    <span>{row.roi}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
